import { graphql, useStaticQuery } from "gatsby"
import BackgroundImage from "gatsby-background-image"
import Img from "gatsby-image"
import React from "react"
import ReactPlayer from "react-player"
import Slider from "react-slick"
import styled, { css } from "styled-components"
import Breakpoints from "../../../components/shared/breakpoints"
import FadedTitle from "../../../components/shared/faded-title"
import {
  adContentImageStyle,
  ImageContainer,
} from "../../../components/web-app/feature-highlights.style"
import Button from "../../atoms/button"
import { Block, Grid, Spacer } from "../../atoms/layout-primitives"
import ContentBlock from "../../molecules/content-block"
import FeatureCard from "../../molecules/feature-card/feature-card"
import { FadeIn, FadeInBottom } from "../../styles/animations.styles"
import { T } from "../../styles/typography"
import withAnimation from "../../util/withAnimation"

const Span = styled.span`
  display: inline-block;
`

const AnimatedSpan = withAnimation(Span)

const HighlightTitle = styled(AnimatedSpan)`
  display: inline;
  color: var(--burnt-orange);
  font-weight: 500;
`
const HighlightText = styled(AnimatedSpan)`
  @media (min-width: ${Breakpoints.lg}) {
    display: inline;
    color: var(--burnt-orange);
    font-weight: 500;
  }
`
const blueDot = css`
  background-color: yellow;
  width: 48;
`

export default function MappingSolutionsSection() {
  const query = useStaticQuery(graphql`
    query {
      introImage: file(
        relativePath: { eq: "1_SOLUTIONS/Imdf/1_Desktop/intro_image.jpg" }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }

      digitalDirectoryImage: file(
        relativePath: { eq: "1_SOLUTIONS/Imdf/1_Desktop/intro_image.jpg" }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 95) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      indoorMappingImage: file(
        relativePath: { eq: "1_SOLUTIONS/Imdf/1_Desktop/indoor_mapping.jpg" }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 95) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      blueDotLogo: file(
        relativePath: { eq: "1_SOLUTIONS/Imdf/1_Desktop/blue_dot_logo.svg" }
      ) {
        publicURL
      }
      blueprintBackground: file(
        relativePath: {
          eq: "1_SOLUTIONS/Imdf/1_Desktop/blueprint_background.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 95) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      fadedTitle: file(
        relativePath: { eq: "1_SOLUTIONS/Imdf/1_Desktop/faded_title.svg" }
      ) {
        publicURL
      }
      consistentExperienceImages: allFile(
        filter: {
          relativeDirectory: { eq: "1_SOLUTIONS/Imdf/1_Desktop/gallery" }
        }
        sort: { fields: base }
      ) {
        edges {
          node {
            id
            base
            childImageSharp {
              fluid(maxWidth: 1920, quality: 100) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
    }
  `)

  return (
    <>
      <Grid>
        <Block
          row={{ sm: "1" }}
          col={{ sm: "1 / -1", md: "2 / -2", lg: "-6 / -1" }}
          align={{ lg: "center" }}
        >
          <Spacer height={{ sm: "32px", md: "64px", lg: "0px" }} />

          <ContentBlock.H3
            title={<AnimatedSpan animation={FadeIn}>IMDF Export</AnimatedSpan>}
            description={
              <AnimatedSpan animation={FadeInBottom}>
                <HighlightText>Indoor Mapping Data Format (IMDF)</HighlightText>{" "}
                makes it possible for anyone to create indoor map apps and
                services using the same highly accurate and detailed data across
                any website, app or operating system. Mappedin’s IMDF export has
                been fully approved through Apple, following the recognized
                Community Standard.
                <br></br>
                <br></br>
                Mappedin provides indoor mapping for{" "}
                <HighlightText>
                  700+ venues in 30+ countries around the world.
                </HighlightText>{" "}
                Our industry-leading tools power
                <HighlightText> 550,000,000+ square feet </HighlightText> of
                data, wayfinding, and indoor maps.
              </AnimatedSpan>
            }
            variants={[null, { textAlign: "center" }, { textAlign: "left" }]}
          />
        </Block>
        <Spacer height={{ sm: "32px", md: "48px", lg: "0px" }} />
        <Block.Animated
          animation={FadeInBottom}
          row={{ lg: "1" }}
          col={{ sm: "1 / -1", md: "3 / -3", lg: "1 / 8" }}
          align={{ lg: "center" }}
          justify={{ md: "center" }}
          variants={[
            null,
            css`
              & .gatsby-image-wrapper {
                max-height: 575px;
              }
            `,
            css`
              & .gatsby-image-wrapper {
                max-height: unset;
              }
            `,
          ]}
        >
          <Img
            fluid={query.introImage.childImageSharp.fluid}
            imgStyle={{ objectFit: "contain" }}
            alt="A laptop displaying Mappedin Developer Tools"
          />
        </Block.Animated>
      </Grid>

      {/* Accent Title Section */}
      <Grid>
        <Spacer height={{ sm: "64px", md: "48px", lg: "96px" }} />
        <Block>
          <FadedTitle
            sm={css`
              ${T.sm.h2`500`};
              grid-column: 1 / -1;
              text-align: center;
              color: var(--black);
            `}
            md={css`
              grid-column: 4 / 8;
              ${T.md.h2`500`}
            `}
            lg={css`
              ${T.lg.h2`500`}
            `}
            title="Showcase Your Indoor Maps on Apple Maps"
            fadeSettings={{ lg: "center" }}
            textAlign="center"
            style={{ width: "100%", color: "black" }}
            image={query.fadedTitle.publicURL}
          />
          <Spacer height={{ sm: "32px", md: "32px", lg: "48px" }} />
          <Button.ScrollLink
            to="contact-form"
            variants={{ sm: { margin: "0 auto" } }}
            smooth
            duration={1000}
          >
            Contact Us
          </Button.ScrollLink>
          <Spacer height={{ sm: "64px", md: "96px", lg: "96px" }} />
        </Block>
      </Grid>
      {/* Versatile Applications */}

      <Grid>
        <Block
          row={{ sm: "3", lg: "1" }}
          col={{ sm: "1 / -1", md: "2 / -2", lg: "2 / 7" }}
          align={{ lg: "center" }}
          variants={[null, { textAlign: "center" }, { textAlign: "left" }]}
        >
          <ContentBlock.H3
            title={
              <AnimatedSpan animation={FadeIn}>
                Versatile <HighlightTitle>Applications</HighlightTitle>
              </AnimatedSpan>
            }
            Digital
            Directory
            description={
              <AnimatedSpan animation={FadeInBottom}>
                Regardless of industry, Mappedin provides you with a simplified
                workflow to showcase your venue floor plans on Apple Maps. We've
                worked with shopping malls, office buildings, stadiums,
                campuses, grocery stores, retailers, and more around the globe.
                <br></br>
                <br></br>
                Along with the Apple Maps platform, Mappedin enables indoor
                mapping experiences across your entire portfolio of digital
                touch points. From your website, to mobile applications and
                touchscreen digital directories, visitors can rely on an
                accurate and intuitive mapping experience.
              </AnimatedSpan>
            }
          />
        </Block>
        <Spacer height={{ sm: "32px", md: "64px", lg: "96px" }} />

        <Block.Animated
          row={{ sm: "1" }}
          col={{ sm: "1 / -1", md: "3 / -3", lg: "-6 / -1" }}
          align={{ lg: "center" }}
          variants={[{ paddingLeft: "2.5%" }]}
        >
          {}
          <Slider dots arrows>
            {query.consistentExperienceImages.edges.map(({ node }) => (
              <Img
                key={node.id}
                fluid={node.childImageSharp.fluid}
                imgStyle={{ objectFit: "contain" }}
                alt={node.base}
              />
            ))}
          </Slider>
        </Block.Animated>
        <Spacer height={{ sm: "32px", md: "64px", lg: "96px" }} />
      </Grid>

      {/*Mapping CMS */}

      <Grid>
        <Block
          row={{ sm: "3", lg: "1" }}
          col={{ sm: "1 / -1", md: "2 / -2", lg: "-6 / -1" }}
          align={{ sm: "center" }}
          variants={[null, { textAlign: "center" }, { textAlign: "left" }]}
        >
          <ContentBlock.H2
            title={
              <AnimatedSpan animation={FadeIn}>
                Leader in <HighlightTitle>Indoor Mapping</HighlightTitle>
              </AnimatedSpan>
            }
            description={
              <AnimatedSpan animation={FadeIn}>
                Apple has made their indoor mapping program available to all,
                meaning anyone can submit data through IMDF. The difference with
                Mappedin is our expertise and experience with indoor venues, a
                dedication to heightened data standards, process automation,
                constant collaboration with Apple, and the ability to maintain
                your venue maps through one platform.
              </AnimatedSpan>
            }
          />
        </Block>
        <Spacer height={{ sm: "32px", md: "48px", lg: "0px" }} />
        <Block.Animated
          animation={FadeInBottom}
          row={{ sm: "1" }}
          col={{ sm: "1 / -1", md: "3 / -3", lg: "1 / 7" }}
          align={{ lg: "center" }}
        >
          <Img
            fluid={query.indoorMappingImage.childImageSharp.fluid}
            alt="A desktop computer showing the Mappedin Content Management System"
            imgStyle={{ objectFit: "contain" }}
          />
        </Block.Animated>
      </Grid>
      <Spacer height={{ sm: "64px", lg: "96px" }} />
      <BackgroundImage
        fluid={query.blueprintBackground.childImageSharp.fluid}
        fadeIn={false}
      >
        <Spacer height={{ sm: "48px", lg: "96px" }} />
        <Grid>
          <Block
            row={{ sm: "1" }}
            col={{ sm: "1 / -1", md: "2 / -2", lg: "2 / 7" }}
            align={{ lg: "center" }}
            variants={[null, { textAlign: "center" }, { textAlign: "left" }]}
          >
            <FeatureCard
              featureDetails={{
                icon: query.blueDotLogo.publicURL,
                title: "Blue Dot Indoor Positioning",
                text: (
                  <>
                    Unlock infrastructure-free indoor positioning through IMDF,
                    fingerprinting, and Mappedin's front-end experiences. Our
                    solutions include turn-by-turn wayfinding and blue dot
                    positioning capabilities. Using Mappedin's Responsive Web
                    App, visitors can access their location directly in the
                    browser. This experience is seamless and does not rely on
                    hardware installations or application downloads.
                  </>
                ),
              }}
              titleVariants={[
                null,
                css`
                  line-height: 140%;
                  font-size: 20px;
                `,
                css`
                  font-size: 36px;
                `,
              ]}
              iconVariants={[
                css`
                  max-width: unset !important;
                  max-height: unset !important;
                  width: 64px;
                  height: 64px;
                `,
                css`
                  margin: 0 0 32px 0;
                `,
                css`
                  width: 84px;
                  height: 84px;
                  margin: 0 32px 0 0;
                `,
              ]}
              headlineLayoutVariants={{
                md: { flexDirection: "column" },
                lg: { flexDirection: "row" },
              }}
            />
          </Block>

          <Spacer height={{ sm: "32px", md: "48px", lg: "0px" }} />

          <Block.Animated
            row={{ lg: "1" }}
            col={{ sm: "1 / -1", md: "3 / -3", lg: "-6 / -1" }}
            align={{ lg: "center" }}
            variants={[{ paddingLeft: "2.5%" }]}
          >
            <Spacer />
            <ImageContainer styles={adContentImageStyle}>
              <ReactPlayer
                url={"https://vimeo.com/488132212/617c75518c"}
                playing={true}
                loop={true}
                volume={0}
                muted={true}
                controls={false}
                width={"100%"}
                height={"400px"}
                playsinline={true}
              />
            </ImageContainer>
          </Block.Animated>
        </Grid>
        <Spacer height={{ sm: "48px", md: "64px", lg: "96px" }} />
      </BackgroundImage>
    </>
  )
}
